import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { Input, Modal, Space, Typography } from 'antd'
import {
  errorNotification,
  postRequest,
  successNotification,
  trackingShipmentActionUrl
} from '../../config'
import {
  buildClosePayload,
  buildNotePayload,
  buildResolvePayload,
  errorSentence
} from './utils'

// Four actions that all come down to "say why, in writing": resolving an alert, closing a shipment,
// leaving a note and recording that ShipsGo was asked for a credit back. One dialog, because the
// only thing that differs is the sentence above the box and where the note is posted — and because
// four near-identical files are four places for the note to stop being required.
//
// The note is internal in every mode. Only Close can also carry words the customer reads, and that
// is a separate field for exactly that reason.
const MODES = {
  resolve: {
    title: 'Resolve the alert',
    standing:
      'Clears the open alert and puts this shipment back to tracking. The customer\'s app stops showing the alert.',
    okText: 'Resolve',
    danger: false,
    dataName: 'resolve'
  },
  close: {
    title: 'Close this shipment',
    standing:
      'Ends tracking for the customer. The app shows it as closed, with no further updates. It cannot be reopened from the dashboard. Closing never refunds — record any refund first. The note is internal; the customer never sees it.',
    okText: 'Close shipment',
    danger: true,
    dataName: 'close'
  },
  note: {
    title: 'Add a note',
    standing: 'Visible to the team only.',
    okText: 'Add note',
    danger: false,
    dataName: 'note'
  },
  creditRefund: {
    title: 'ShipsGo credit refund requested',
    standing:
      'Records that you asked ShipsGo for a credit back. Make the request with ShipsGo support; this does not contact them. Put the ticket or email reference in the note.',
    okText: 'Record request',
    danger: false,
    dataName: 'credit refund request'
  }
}

const NoteActionModal = ({ visible, mode, shipment, onClose, onDone }) => {
  const row = shipment || {}
  const config = MODES[mode] || MODES.note
  const [note, setNote] = useState('')
  const [customerMessage, setCustomerMessage] = useState('')
  const [errors, setErrors] = useState([])
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!visible) return
    setNote('')
    setCustomerMessage('')
    setErrors([])
    setSaving(false)
  }, [visible, mode])

  const send = async () => {
    if (saving) return
    setSaving(true)
    let url = trackingShipmentActionUrl(row.id, 'actions')
    let payload = buildNotePayload('note', note)
    if (mode === 'resolve') {
      url = trackingShipmentActionUrl(row.id, 'resolve')
      payload = buildResolvePayload(note)
    } else if (mode === 'close') {
      url = trackingShipmentActionUrl(row.id, 'close')
      payload = buildClosePayload(note, customerMessage)
    } else if (mode === 'creditRefund') {
      payload = buildNotePayload('credit_refund_requested', note)
    }
    const response = await postRequest(url, payload, config.dataName, () => {})
    setSaving(false)
    if (!response.success) {
      errorNotification(errorSentence(response, 'That did not work.'))
      return
    }
    successNotification('Done.')
    onDone()
  }

  const submit = () => {
    if (saving) return
    const found = []
    if (!note.trim()) found.push('Add a note.')
    if (mode === 'close' && customerMessage.trim().length > 500) {
      found.push('Keep the message under 500 characters.')
    }
    setErrors(found)
    if (found.length) return
    if (mode === 'close') {
      // Closing ends tracking for a customer who paid for it, and the dashboard cannot reopen it.
      // The second confirm names the number so it is not the wrong shipment that ends.
      Modal.confirm({
        title: `Close ${row.number}? This cannot be undone here.`,
        okText: 'Close shipment',
        okType: 'danger',
        onOk: send
      })
      return
    }
    send()
  }

  return (
    <Modal
      visible={visible}
      title={config.title}
      onCancel={onClose}
      onOk={submit}
      okText={config.okText}
      okButtonProps={{ danger: config.danger, disabled: saving }}
      confirmLoading={saving}
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <div style={{ opacity: 0.65 }}>{config.standing}</div>

        <div>
          <div>Note</div>
          <Input.TextArea
            rows={3}
            maxLength={1000}
            showCount
            value={note}
            onChange={event => setNote(event.target.value)}
          />
        </div>

        {mode === 'close' ? (
          <div>
            <div>Message to the customer</div>
            <Input.TextArea
              rows={2}
              maxLength={500}
              showCount
              value={customerMessage}
              onChange={event => setCustomerMessage(event.target.value)}
            />
            <div style={{ opacity: 0.65 }}>
              {'Shown in their app. Leave blank for the standard \'tracking has been closed\' message.'}
            </div>
          </div>
        ) : null}

        {errors.length ? (
          <div>
            {errors.map(message => (
              <div key={message}>
                <Typography.Text type="danger">{message}</Typography.Text>
              </div>
            ))}
          </div>
        ) : null}
      </Space>
    </Modal>
  )
}

NoteActionModal.propTypes = {
  visible: PropTypes.bool,
  mode: PropTypes.string,
  shipment: PropTypes.object,
  onClose: PropTypes.func,
  onDone: PropTypes.func
}

export default NoteActionModal
